// App
import type React from 'react';
import { useEffect, useState } from 'react';

import { parseQueryString } from '@zg-rentals/string-utils';

// Constellation
import { Box, Button, FormHelp, Input, Label, LabeledInput, Paragraph, Spinner } from '@zillow/constellation-10';

import { useGetSearch } from '../../hooks/queries/search';
import { useGetUser } from '../../hooks/queries/user';
import { useGetAddress } from '../../hooks/queries/address';

import { fetchAutocompleteData } from '../../flux/actions/AddressActions';

// @ts-ignore
import { augmentError } from '../../utils/errors';

import constants from '../../constants/ConstantsBundle';

import { useLocation } from '../../hooks/useLocation';

import { useEmail, useGoToAddressItem, useSuggestions } from './hooks';

import { addressInputContainerStyles, disclaimerStyles } from './styles';

import Autocomplete from '../Autocomplete';
import NoZestimateModal from '../NoZestimateModal';

// Types
import type AutocompleteAddress from '../../models/AutocompleteAddress';
import { emitContinueAboveFoldEvent } from '../../analytics/priceMyRentalEvents';

// const logger = getLogger('modules/PriceMyRentalSearchModule');

const PriceMyRentalSearchModule = ({ customTracking }: { customTracking?: () => void }) => {
  // react hooks
  const [addressInputValue, updateAddressInputValue] = useState('');
  const [unit, updateUnit] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [validAddressItem, updateValidAddressItem] = useState<AutocompleteAddress | null>(null);

  // custom hooks
  const { data: user } = useGetUser();
  const { loggedIn: isLoggedIn, isInitialMultiFactorAuthenticationRequired, email: userEmail } = user;
  const isPartiallyAuthenticated = isLoggedIn || isInitialMultiFactorAuthenticationRequired;

  const { data: recentSearches = [], addRecentSearch } = useGetSearch();

  const { setAddress } = useGetAddress();

  const { email, updateEmail, validateEmail, emailError } = useEmail();
  const { addressError, isNoZestModalOpen, openNoZestModal, updateAddressError, validateAndGoToAddressItem } =
    useGoToAddressItem();
  const { suggestions, getSuggestions } = useSuggestions(recentSearches as Array<AutocompleteAddress>);

  const location = useLocation();
  const { address: prefillAddress } = parseQueryString(location.search) as { address?: string };

  // Prefill the search box from the `address` query param (set when a user is
  // redirected here from a legacy PMR result URL) and auto-select the top
  // autocomplete match so they can continue without re-selecting from the dropdown.
  useEffect(() => {
    if (!prefillAddress) {
      return;
    }

    updateAddressInputValue(prefillAddress);

    fetchAutocompleteData(prefillAddress)()
      .then((results) => {
        const topMatch = results?.[0];
        if (topMatch) {
          updateAddressInputValue(topMatch.name || '');
          updateAddressError('');
          updateValidAddressItem(topMatch);
        }
        return null;
      })
      .catch(() => {
        // Leave the raw prefill in the box; the user can refine and select manually.
      });
    // Run once on mount with the address from the landing URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // action handlers
  const handleAddressChange = (newAddressInputValue: string) => {
    updateAddressInputValue(newAddressInputValue);
    getSuggestions(newAddressInputValue);
  };
  const handleAddressSelect = (addressItem: AutocompleteAddress) => {
    // update input value when selection is made
    updateAddressInputValue(addressItem.name || '');
    // reset address input error
    updateAddressError('');
    updateValidAddressItem(addressItem);
  };

  const handleNoZestModalClose = () => {
    openNoZestModal(false);
  };

  const handleEmailKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.which === constants.ENTER_KEYCODE) {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    analyticsTracking();

    validateAndGoToAddressItem({
      addressInputValue,
      isPartiallyAuthenticated,
      email,
      setIsLoading,
      unit,
      validAddressItem,
      validateEmail,
      addRecentSearch,
      setAddress,
    }).catch((err: Error) => {
      augmentError(err, {
        root: 'modules/PriceMyRentalSearchModule#handleSubmit',
      });

      // logger.error(formatForLog(err), 'Landing page: Error loading address/comps from search module');
    });
  };

  const analyticsTracking = () => {
    if (customTracking) {
      customTracking();
    } else {
      emitContinueAboveFoldEvent({ user });
    }
  };

  return (
    <div data-testid="PriceMyRentalSearchModule">
      <Box css={addressInputContainerStyles}>
        <Autocomplete
          items={suggestions}
          onChange={handleAddressChange}
          onSelect={handleAddressSelect}
          label={<Label>Street address</Label>}
          value={addressInputValue}
        />

        <Box css={{ float: 'right', maxWidth: '96px' }}>
          <form data-autocomplete="off">
            <LabeledInput
              input={
                <Input
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    // @ts-ignore https://github.com/desktop/desktop/issues/297
                    updateUnit(e.target.value)
                  }
                />
              }
              label={<Label>Unit</Label>}
            />
          </form>
        </Box>
      </Box>

      <Box css={{ display: 'flex', flexDirection: 'column' }}>
        {Boolean(!isLoggedIn && !userEmail) && (
          <Box css={{ paddingBottom: '200' }}>
            <LabeledInput
              input={
                <Input
                  autoComplete="email"
                  error={Boolean(emailError)}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    // @ts-ignore https://github.com/desktop/desktop/issues/297
                    updateEmail(e.target.value)
                  }
                  onKeyDown={handleEmailKeyDown}
                />
              }
              label={<Label>Email address</Label>}
            />
          </Box>
        )}

        {(addressError || emailError) && (
          <FormHelp css={{ padding: '5px 0', float: 'left' }} error={true}>
            {addressError || emailError}
          </FormHelp>
        )}

        {Boolean(!isLoggedIn && !userEmail) && (
          <Paragraph css={disclaimerStyles}>
            By providing your email address, you agree to receive promotional and marketing materials from Zillow.
          </Paragraph>
        )}
      </Box>

      <NoZestimateModal
        address={addressInputValue}
        validAddressItem={validAddressItem ?? undefined}
        isOpen={isNoZestModalOpen}
        onClose={handleNoZestModalClose}
      />

      <Box css={{ float: 'left' }}>
        <Button tone="brand" emphasis="filled" onClick={handleSubmit}>
          Continue
        </Button>

        {isLoading && <Spinner size="lg" css={{ position: 'absolute', marginLeft: '200' }} />}
      </Box>
    </div>
  );
};

export default PriceMyRentalSearchModule;
